// Сколько стоит помощник — по клиентам и по продукту (Э7 плана помощника).
//
// Зачем. Токены пишутся в `ai_usage` с первого дня, но их никто не читал:
// оператор не знал, во сколько обходится каждый клиент. Продаём ИИ-модуль
// отдельной строкой за 5 000 ₽ в месяц — значит маржу надо видеть, а не
// предполагать. Считает всё код, отчёт только складывает.
//
// 🔴 Цена НЕ ЗАШИТА в продукт. Тариф поставщика меняется, курс тоже, и
// «примерно посчитанные» деньги в интерфейсе оператора хуже, чем честное
// «цена не задана»: по ним принимают решение о стоимости подписки. Ставится
// переменной `AI_RUB_PER_MTOKEN` — рублей за миллион токенов.

/** Рублей за миллион токенов; null — цена не задана, деньги не показываем. */
export function rubPerMillionTokens(env: Record<string, string | undefined> = process.env): number | null {
  const raw = env.AI_RUB_PER_MTOKEN?.trim();
  if (!raw) return null;
  const n = Number(raw.replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Деньги за объём токенов. Null — когда цена не задана. */
export function tokensToRub(tokens: number, perMillion: number | null): number | null {
  if (perMillion === null || !Number.isFinite(tokens) || tokens <= 0) return perMillion === null ? null : 0;
  return Math.round((tokens / 1_000_000) * perMillion * 100) / 100;
}

/**
 * Записать вопрос, на который не ответили.
 *
 * Пишем ТИХО: журнал приоритетов не должен мешать ответу человеку. Упала
 * запись — человек этого не замечает, мы теряем одну строку статистики, и это
 * несравнимо дешевле, чем уронить ответ ради неё.
 */
export async function noteGap(
  // Тип — минимальный контракт, а не PrismaClient: так функция проверяется
  // тестом без базы и без мока всего клиента.
  db: { aiGap: { create: (args: { data: { tenant_id: string; day: string; question: string; kind: string } }) => Promise<unknown> } },
  tenantId: string,
  question: string,
  kind: "unknown" | "clarify",
): Promise<void> {
  const text = question.trim().slice(0, 200);
  if (!text) return;
  const now = new Date();
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  try {
    await db.aiGap.create({ data: { tenant_id: tenantId, day, question: text, kind } });
  } catch (e) {
    console.warn("[ai] не записал пробел в умениях", e);
  }
}

export type SpendRow = {
  tenantId: string;
  /** Имя и адрес — чтобы оператор узнал клиента, а не читал идентификатор. */
  name: string;
  slug: string;
  calls: number;
  tokens: number;
  /** Рубли за период; null — цена за токены не задана. */
  rub: number | null;
  /** Дней, в которые к помощнику вообще обращались: «пользуются или нет». */
  activeDays: number;
  /** Сколько платит клиент в месяц — для сравнения с расходом. */
  monthlyFee: number | null;
};

export type SpendReport = {
  from: string;
  to: string;
  rows: SpendRow[];
  totals: { calls: number; tokens: number; rub: number | null; tenants: number };
  /** Цена, по которой считали, — чтобы оператор видел, откуда рубли. */
  rubPerMillionTokens: number | null;
};

/**
 * Сложить построчную статистику в отчёт.
 *
 * Вынесено из действия и работает на чистых данных: деньги — тот случай, где
 * ошибка стоит дороже всего (правило №4 CLAUDE.md), и проверять их надо
 * тестом, а не глазами в консоли оператора.
 */
export function buildSpendReport(
  usage: readonly { tenant_id: string; day: string; calls: number; tokens: number }[],
  tenants: readonly { id: string; name: string; slug: string; monthly_fee: number | null }[],
  period: { from: string; to: string },
  perMillion: number | null,
): SpendReport {
  const byTenant = new Map<string, { calls: number; tokens: number; days: Set<string> }>();
  for (const u of usage) {
    const cur = byTenant.get(u.tenant_id) ?? { calls: 0, tokens: 0, days: new Set<string>() };
    cur.calls += u.calls;
    cur.tokens += u.tokens;
    // День считается активным по ОБРАЩЕНИЯМ, а не по строке: строка может
    // появиться и с нулём, если вопрос не дошёл до модели.
    if (u.calls > 0) cur.days.add(u.day);
    byTenant.set(u.tenant_id, cur);
  }

  const nameOf = new Map(tenants.map((t) => [t.id, t]));
  const rows: SpendRow[] = [...byTenant.entries()].map(([tenantId, v]) => {
    const t = nameOf.get(tenantId);
    return {
      tenantId,
      // Удалённый клиент из статистики не пропадает: деньги мы за него уже
      // заплатили, и в отчёте за прошлый месяц он обязан быть виден.
      name: t?.name ?? "удалённый клиент",
      slug: t?.slug ?? "—",
      calls: v.calls,
      tokens: v.tokens,
      rub: tokensToRub(v.tokens, perMillion),
      activeDays: v.days.size,
      monthlyFee: t?.monthly_fee ?? null,
    };
  });
  // Сверху самые дорогие: отчёт открывают, чтобы увидеть, кто расходует.
  rows.sort((a, b) => b.tokens - a.tokens);

  const tokens = rows.reduce((s, r) => s + r.tokens, 0);
  return {
    from: period.from,
    to: period.to,
    rows,
    totals: {
      calls: rows.reduce((s, r) => s + r.calls, 0),
      tokens,
      rub: tokensToRub(tokens, perMillion),
      tenants: rows.length,
    },
    rubPerMillionTokens: perMillion,
  };
}

// ── Калькулятор денег (28.09, заказ Марата) ──
//
// Отчёт выше отвечает «сколько токенов». Оператору нужен ответ в рублях: во
// сколько обходится помощник всего и по каждому клиенту, сколько это выйдет за
// месяц и что остаётся от ИИ-абонплаты. Цену за токены оператор может
// подставить прямо в консоли (по умолчанию — из AI_RUB_PER_MTOKEN), поэтому
// пересчёт живёт здесь, в чистой функции под тестом, а не в разметке.

/** Дней в «месяце» прогноза: средний месяц, чтобы февраль не занижал прогноз. */
export const DAYS_PER_MONTH = 30.4;

export type SpendMoneyRow = {
  tenantId: string;
  name: string;
  slug: string;
  calls: number;
  tokens: number;
  /** Рубли за выбранный период. */
  costPeriod: number | null;
  /** Прогноз на месяц при том же темпе. */
  costMonth: number | null;
  /** Средняя цена одного вопроса. */
  perCall: number | null;
  /** ИИ-абонплата клиента за месяц минус прогноз расхода. */
  marginMonth: number | null;
};

export type SpendMoney = {
  rows: SpendMoneyRow[];
  totals: {
    calls: number;
    tokens: number;
    costPeriod: number | null;
    costMonth: number | null;
    /** Сколько приносит ИИ-абонплата за месяц — с клиентов, задававших вопросы. */
    feeMonth: number;
    /** Сколько таких клиентов. */
    payers: number;
    marginMonth: number | null;
    perCall: number | null;
  };
};

const kop = (v: number) => Math.round(v * 100) / 100;

/**
 * Пересчитать отчёт в деньги по заданной цене.
 *
 * `periodDays` — длина периода отчёта в календарных днях: прогноз на месяц —
 * средний расход в день × 30,4. `aiFee` — ИИ-абонплата одного клиента в месяц;
 * маржа считается для каждого клиента, у которого был расход.
 */
export function spendMoney(
  rows: readonly Pick<SpendRow, "tenantId" | "name" | "slug" | "calls" | "tokens">[],
  opts: { perMillion: number | null; periodDays: number; aiFee: number },
): SpendMoney {
  const days = Math.max(1, Math.trunc(opts.periodDays) || 1);
  const fee = Number.isFinite(opts.aiFee) && opts.aiFee > 0 ? opts.aiFee : 0;
  const price = opts.perMillion !== null && Number.isFinite(opts.perMillion) && opts.perMillion > 0 ? opts.perMillion : null;
  const money = (tokens: number) => (price === null ? null : (tokens / 1_000_000) * price);

  const out: SpendMoneyRow[] = rows.map((r) => {
    const cost = money(r.tokens);
    const month = cost === null ? null : (cost / days) * DAYS_PER_MONTH;
    return {
      tenantId: r.tenantId,
      name: r.name,
      slug: r.slug,
      calls: r.calls,
      tokens: r.tokens,
      costPeriod: cost === null ? null : kop(cost),
      costMonth: month === null ? null : kop(month),
      perCall: cost === null || r.calls <= 0 ? null : kop(cost / r.calls),
      // Абонплату считаем только тем, кто задавал вопросы: у остальных токены —
      // фоновые разборы (отзывы, опердир), модуль ими не куплен.
      marginMonth: month === null || r.calls <= 0 ? null : kop(fee - month),
    };
  });

  const calls = rows.reduce((s, r) => s + r.calls, 0);
  const tokens = rows.reduce((s, r) => s + r.tokens, 0);
  const cost = money(tokens);
  const month = cost === null ? null : (cost / days) * DAYS_PER_MONTH;
  const payers = rows.filter((r) => r.calls > 0).length;
  const feeMonth = fee * payers;
  return {
    rows: out,
    totals: {
      calls,
      tokens,
      costPeriod: cost === null ? null : kop(cost),
      costMonth: month === null ? null : kop(month),
      feeMonth,
      payers,
      marginMonth: month === null ? null : kop(feeMonth - month),
      perCall: cost === null || calls <= 0 ? null : kop(cost / calls),
    },
  };
}
